import axios from 'axios'
import DOMPurify from 'dompurify'
import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useT } from '_metronic/i18n/Metronici18n'
import { ButtonType, showError } from '../../../../_metronic/helpers'
import { Button, LoadingIndicator, LogoComponent } from '../../../components'
import { TermoConsentimento } from 'app/modules/apps/termos/core/_models'
import { aceitarTermoUsuario, getTermoUsuarioVigente } from 'app/modules/apps/termos/core/_requests'
import styles from 'app/modules/apps/termos/termos-list/components/styles/TermoDetailModal.module.css'
import { useAuth } from '../core/Auth'

/**
 * Termo de ciência (LGPD) no primeiro acesso e a cada versão nova publicada.
 *
 * Enquanto `currentUser.termoCienciaPendente` for verdadeiro, o `AppRoutes` desvia toda rota
 * privada para cá e o backend responde 403 `TERMO_CIENCIA_PENDENTE` em tudo, menos no termo, no
 * aceite, no logout e na troca de senha. É ciência, não consentimento: um clique registrado com
 * data, IP, user agent, versão e hash do texto é a evidência exigida.
 *
 * Fica fora do `AuthLayout` de propósito: a coluna de 450px do login não comporta um texto legal.
 */
export const TermoCiencia = () => {
  const { t } = useT()
  const { currentUser, reloadUser, logout } = useAuth()
  const [termo, setTermo] = useState<TermoConsentimento>()
  const [carregando, setCarregando] = useState(true)
  const [enviando, setEnviando] = useState(false)
  // 404 = nenhum termo de usuário publicado: não há o que aceitar, a sessão segue livre.
  const [semTermo, setSemTermo] = useState(false)

  useEffect(() => {
    let ativo = true
    getTermoUsuarioVigente()
      .then((dados) => {
        if (ativo) setTermo(dados)
      })
      .catch((error) => {
        if (axios.isAxiosError(error) && error.response?.status === 404) setSemTermo(true)
        else showError(error)
      })
      .finally(() => {
        if (ativo) setCarregando(false)
      })
    return () => {
      ativo = false
    }
  }, [])

  if (!currentUser) return <Navigate to='/auth' replace />
  // Depois do aceite o `reloadUser` zera a pendência e esta mesma guarda devolve o usuário ao app.
  if (!currentUser.termoCienciaPendente || semTermo) return <Navigate to='/' replace />

  const confirmar = async () => {
    if (!termo?.id) return
    setEnviando(true)
    try {
      await aceitarTermoUsuario({ idTermo: termo.id })
      await reloadUser()
    } catch (error) {
      showError(error)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className='d-flex flex-column flex-center min-vh-100 p-5 p-lg-10'>
      <div className='w-100 mw-800px'>
        <div className='d-flex justify-content-center mb-8'>
          <LogoComponent className='h-50px' />
        </div>

        <h1 className='text-dark fs-2x mb-3'>{t('termo_ciencia.titulo')}</h1>
        <p className='text-gray-700 fs-5 mb-6'>{t('termo_ciencia.descricao')}</p>

        {carregando ? (
          <div className='d-flex align-items-center gap-3 text-gray-700' role='status'>
            <LoadingIndicator />
            {t('termo_ciencia.carregando')}
          </div>
        ) : (
          termo && (
            <>
              <div className='d-flex justify-content-between align-items-baseline flex-wrap gap-2 mb-3'>
                <h2 className='fs-4 mb-0'>{termo.titulo}</h2>
                <span className='text-gray-600 fs-7'>
                  {t('termo_ciencia.versao', { versao: termo.versao })}
                </span>
              </div>
              {/* HTML do termo vem da API: sempre sanitizado (regra do projeto). */}
              <div
                className={styles.conteudo}
                tabIndex={0}
                aria-label={t('termo_ciencia.conteudo_aria')}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(termo.conteudo) }}
              />
            </>
          )
        )}

        <div className='d-flex justify-content-between align-items-center flex-wrap gap-3 pt-8'>
          <Button
            variant='secondary-outline'
            type={ButtonType.Button}
            onClick={logout}
            disabled={enviando}
          >
            {t('termo_ciencia.sair')}
          </Button>
          <Button
            className='btn-primary'
            type={ButtonType.Button}
            onClick={confirmar}
            disabled={carregando || enviando || !termo}
          >
            {enviando ? <LoadingIndicator /> : t('termo_ciencia.confirmar')}
          </Button>
        </div>
      </div>
    </div>
  )
}
